import { ACTIVITY_LANGUAGES, PARTICIPATIONS } from '@yoyoclub/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AgeInput } from './AgeInput';
import { CheckboxMenu, Chip, FilterButton } from './filterControls';
import { ageRangeLabel, whenPresetLabel, WHEN_PRESETS } from './filterLabels';
import {
  EMPTY_FILTERS,
  FilterState,
  isAgeActive,
  isFilterActive,
  toggled,
  withAge,
  withParticipationToggled,
} from './filterState';

const CATEGORY_VALUES = [
  'ART',
  'MUSIC',
  'DANCE',
  'SPORTS',
  'COOKING',
  'STEM',
  'NATURE',
  'OTHER',
] as const;
const REGION_VALUES = ['SOUTH_BAY', 'PENINSULA', 'EAST_BAY', 'SAN_FRANCISCO', 'OTHER'] as const;

type OpenMenu = null | 'when' | 'age' | 'region' | 'category' | 'language' | 'participation';

interface Props {
  value: FilterState;
  onChange: (next: FilterState) => void;
}

/**
 * Desktop horizontal filter bar with click-outside-to-close dropdowns,
 * plus active-chips row and a "Clear all" link.
 *
 * Mobile rendering is delegated to <ActivitiesFiltersSheet/> which mounts
 * the same controls inside a bottom-sheet modal.
 */
export default function ActivitiesFilters({ value, onChange }: Props) {
  const { t } = useTranslation('activities');
  const { t: tCommon } = useTranslation('common');
  const [open, setOpen] = useState<OpenMenu>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(null);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const set = (patch: Partial<FilterState>) => onChange({ ...value, ...patch });
  const toggleMenu = (menu: Exclude<OpenMenu, null>) => setOpen(open === menu ? null : menu);

  const toggleRegion = (r: string) => set({ regions: toggled(value.regions, r) });
  const toggleCategory = (c: string) => set({ categories: toggled(value.categories, c) });
  const toggleLanguage = (l: string) => set({ languages: toggled(value.languages, l) });
  const toggleParticipation = (p: string) => onChange(withParticipationToggled(value, p));

  const whenLabel = value.whenPreset === 'anytime' ? t('filters.when') : whenPresetLabel(value, t);

  const ageLabel = value.ageRange
    ? ageRangeLabel(value.ageRange, t)
    : isAgeActive(value)
      ? `${t('filters.age')}: ${value.age}`
      : t('filters.age');

  return (
    <div ref={wrapRef} className="space-y-3">
      {/* Filter buttons row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* When */}
        <div className="relative">
          <FilterButton
            label={whenLabel}
            active={value.whenPreset !== 'anytime'}
            onClick={() => toggleMenu('when')}
          />
          {open === 'when' && (
            <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-soft-gray rounded-xl shadow-xl p-2 z-30">
              {WHEN_PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm hover:bg-primary-pale ${
                    value.whenPreset === p ? 'text-primary font-semibold' : 'text-charcoal'
                  }`}
                  onClick={() => set({ whenPreset: p })}
                >
                  {whenPresetLabel({ ...value, whenPreset: p, dateFrom: '', dateTo: '' }, t)}
                </button>
              ))}
              {value.whenPreset === 'custom' && (
                <div className="mt-1 px-2 pb-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 w-6 shrink-0">{t('when.from')}</label>
                    <input
                      type="date"
                      value={value.dateFrom}
                      onChange={(e) => {
                        const next = e.target.value;
                        // If dateFrom moves past dateTo, drag dateTo forward to match
                        // (no native `max` constraint here — keeping users free to shift the
                        // whole range without first clearing dateTo).
                        const clampedTo = value.dateTo && next > value.dateTo ? next : value.dateTo;
                        set({ dateFrom: next, dateTo: clampedTo });
                      }}
                      className="flex-1 min-w-0 text-sm px-2 py-1.5 border border-soft-gray rounded-lg accent-primary"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 w-6 shrink-0">{t('when.to')}</label>
                    <input
                      type="date"
                      value={value.dateTo}
                      min={value.dateFrom || undefined}
                      onChange={(e) => set({ dateTo: e.target.value })}
                      className="flex-1 min-w-0 text-sm px-2 py-1.5 border border-soft-gray rounded-lg accent-primary"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Age: a child's age; a homepage stage range shows as a removable chip */}
        <div className="relative">
          <FilterButton
            label={ageLabel}
            active={isAgeActive(value) || value.ageRange !== null}
            onClick={() => toggleMenu('age')}
          />
          {open === 'age' && (
            <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-soft-gray rounded-xl shadow-xl p-4 z-30">
              <div className="text-xs text-gray-500 mb-2">{t('filters.childAge')}</div>
              <AgeInput
                value={value.age}
                onChange={(age) => onChange(withAge(value, age))}
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Region */}
        <div className="relative">
          <FilterButton
            label={
              value.regions.length > 0
                ? `${t('filters.region')} · ${value.regions.length}`
                : t('filters.region')
            }
            active={value.regions.length > 0}
            onClick={() => toggleMenu('region')}
          />
          {open === 'region' && (
            <CheckboxMenu
              options={REGION_VALUES.map((r) => ({
                value: r,
                label: tCommon(`region.${r}` as never),
              }))}
              selected={value.regions}
              onToggle={toggleRegion}
            />
          )}
        </div>

        {/* Category */}
        <div className="relative">
          <FilterButton
            label={
              value.categories.length > 0
                ? `${t('filters.category')} · ${value.categories.length}`
                : t('filters.category')
            }
            active={value.categories.length > 0}
            onClick={() => toggleMenu('category')}
          />
          {open === 'category' && (
            <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-soft-gray rounded-xl shadow-xl p-3 z-30">
              <div className="flex flex-wrap gap-2">
                {CATEGORY_VALUES.map((c) => {
                  const selected = value.categories.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCategory(c)}
                      className={`px-3 py-1.5 text-sm rounded-full border ${
                        selected
                          ? 'bg-primary text-white border-primary'
                          : 'border-soft-gray hover:border-primary text-charcoal'
                      }`}
                    >
                      {tCommon(`category.${c}` as never)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Language */}
        <div className="relative">
          <FilterButton
            label={
              value.languages.length > 0
                ? `${t('filters.language')} · ${value.languages.length}`
                : t('filters.language')
            }
            active={value.languages.length > 0}
            onClick={() => toggleMenu('language')}
          />
          {open === 'language' && (
            <CheckboxMenu
              options={ACTIVITY_LANGUAGES.map((l) => ({
                value: l,
                label: tCommon(`teachingLanguage.${l}` as never),
              }))}
              selected={value.languages}
              onToggle={toggleLanguage}
            />
          )}
        </div>

        {/* Participation (replaces the old Adult toggle) */}
        <div className="relative">
          <FilterButton
            label={
              value.participations.length > 0
                ? `${t('filters.participation')} · ${value.participations.length}`
                : t('filters.participation')
            }
            active={value.participations.length > 0}
            onClick={() => toggleMenu('participation')}
          />
          {open === 'participation' && (
            <CheckboxMenu
              options={PARTICIPATIONS.map((p) => ({
                value: p,
                label: tCommon(`participation.${p}` as never),
              }))}
              selected={value.participations}
              onToggle={toggleParticipation}
            />
          )}
        </div>

        {/* Clear all */}
        {isFilterActive(value) && (
          <button
            type="button"
            onClick={() => onChange(EMPTY_FILTERS)}
            className="text-sm text-gray-500 hover:text-primary underline ml-auto"
          >
            {t('filters.clearAll')}
          </button>
        )}
      </div>

      {/* Active chips row */}
      {isFilterActive(value) && (
        <div className="flex flex-wrap items-center gap-2">
          {value.regions.map((r) => (
            <Chip
              key={`r-${r}`}
              label={tCommon(`region.${r}` as never)}
              onRemove={() => toggleRegion(r)}
            />
          ))}
          {value.categories.map((c) => (
            <Chip
              key={`c-${c}`}
              label={tCommon(`category.${c}` as never)}
              onRemove={() => toggleCategory(c)}
            />
          ))}
          {value.languages.map((l) => (
            <Chip
              key={`l-${l}`}
              label={tCommon(`teachingLanguage.${l}` as never)}
              onRemove={() => toggleLanguage(l)}
            />
          ))}
          {value.participations.map((p) => (
            <Chip
              key={`p-${p}`}
              label={tCommon(`participation.${p}` as never)}
              onRemove={() => toggleParticipation(p)}
            />
          ))}
          {isAgeActive(value) && (
            <Chip
              label={`${t('filters.age')} ${value.age}`}
              onRemove={() => onChange(withAge(value, ''))}
            />
          )}
          {value.ageRange && (
            <Chip
              label={ageRangeLabel(value.ageRange, t)}
              onRemove={() => set({ ageRange: null })}
            />
          )}
          {value.whenPreset !== 'anytime' && (
            <Chip
              label={whenLabel}
              onRemove={() => set({ whenPreset: 'anytime', dateFrom: '', dateTo: '' })}
            />
          )}
        </div>
      )}
    </div>
  );
}
